import React, { useState, createContext, useContext } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import "./App.css";

/* -------------------- Pages -------------------- */
import DashboardLogin from "./components/DashboardLogin";
import Dashboard from "./components/Dashboard";

import CategoryPage from "./components/CategoryPage";
import SubCategoryPage from "./components/SubCategory/SubCategoryPage";
import ModalPage from "./components/Modal/ModalPage";
import ProductPage from "./components/Products/ProductPage";
import AddProductPage from "./components/Products/AddProductPage";
import OrdersPage from "./components/Orders/OrdersPage";
import TransactionsPage from "./components/Transactions/TransactionsPage";
import CustomersPage from "./components/Customers/CustomersPage";
import CanvaTable from "./components/Canva/CanvaTable";
import DiscountType from "./components/Discount/DiscountType";
import ShipmentMethodsTable from "./components/ShippingMethods/ShipmentMethodsTable";
import ReportTypesTable from "./components/Reports/ReportTypesContext";
import ReportsTable from "./components/Reports/ReportsTable";
import BulkSMSPage from "./components/BulkSMS/BulkSMSPage";
import ReviewsPage from "./components/Reviews/ReviewsPage";
import FAQPage from "./components/FAQ/FAQPage";
import OffersPage from "./components/Offers/Offerspage";
import NotificationsTable from "./components/Notificationss/NotificationsTable";
import SettingsPage from "./components/Settings/SettingsPage";
import TaxPage from "./components/Tax/tax";

/* -------------------- Layout -------------------- */
import DashboardHeader from "./components/DashboardHeader";
import DashboardSidebar from "./components/DashboardSidebar";

/* -------------------- Auth -------------------- */
import { useAuth } from "./components/context/AuthContext";
import ProductTypesPage from "./components/Products/ProductTypesPage";
import ContactAdminPage from "./components/ContactAdminPage";
import Kanbanboard from "./components/kanbanboard";
import BlogAdmin from "./components/Blog/BlogAdmin";
import OrdersComingSoon from "./components/Orders/OrdersComingSoon";
import CustomersComingSoon from "./components/Customers/CustomersComingSoon";
import ScrollToTop from "./components/ScrollToTop";
import TaxSettingsPage from "./components/Settings/Taxsettingspage";
import ProductDetails from "./components/Products/ProductDetails";
import ProductAttributes from "./components/ProductA/Productattributes";
import MaterialsPage from "./components/ProductA/Materialspage";
import ColoursPage from "./components/ProductA/Colourspage";
import StylesPage from "./components/ProductA/Stylespage";
import ForgotPassword from "./components/Forgotpassword";
import OtpVerification from "./components/Otpverification";
import ResetPassword from "./components/Resetpassword";
import OrderTypesPage from "./components/Orders/Ordertypespage";



import ExpensesPage from "./components/Master/ExpensesPage";
import ExpenseVoucherPage from "./components/Vouchers/ExpenseVoucherPage";
import Supplier from "./components/Master/Suppliers";
import PaymentVouchers from "./components/Vouchers/PaymentVocher";
import CreatePaymentVoucher from "./components/Vouchers/CreatePayment";
import StatesPage from "./components/Master/StatesPage";
import Districts from "./components/Master/Districts";
import Mandals from "./components/Master/Mandals";
import Cities from "./components/Master/Cities";
import PurchaseVouchers from "./components/Vouchers/PurchaseVouchers";
import CreatePurchaseVoucher from "./components/Vouchers/CreatePurchaseVoucher";
import Items from "./components/Master/Items";

/* -------------------- UI Context -------------------- */
const AppContext = createContext();

export const useAppContext = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useAppContext must be used inside AppContext");
  return ctx;
};

/* -------------------- Dashboard Layout -------------------- */
const DashboardLayout = ({ children, activeSection }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <AppContext.Provider
      value={{
        isCollapsed,
        setIsCollapsed,
        showLogoutModal,
        setShowLogoutModal,
        isMobileMenuOpen,
        setIsMobileMenuOpen,
      }}
    >
      <div className="flex min-h-screen bg-orange-50 bg-opacity-90">
        <DashboardSidebar activeSection={activeSection} />
        <div
          className={`flex-1 flex flex-col transition-all duration-300
          ${isCollapsed ? "lg:ml-20" : "lg:ml-64"} ml-0`}
        >
          <DashboardHeader />
          <main className="flex-1 overflow-x-auto">{children}</main>
        </div>
      </div>
    </AppContext.Provider>
  );
};

/* -------------------- Helpers -------------------- */
const getActiveSection = (path) => {
  if (path === "/dashboard") return "dashboard";
  if (path.includes("/categories")) return "categories";
  if (path.includes("/subcategories")) return "subcategories";
  if (path.includes("/modals")) return "modals";
  if (path === "/products/add") return "add-product";
  if (path.includes("/products/edit")) return "edit-product";
  if (path.includes("/products")) return "products";
  if (path.includes("/orders")) return "orders";
  if (path.includes("/transactions")) return "transactions";
  if (path.includes("/customers")) return "customers";
  if (path.includes("/canvas")) return "canvas";
  if (path.includes("/shippingmethods")) return "shippingmethods";
  if (path.includes("/bulksms")) return "bulksms";
  if (path.includes("/reviews")) return "reviews";
  if (path.includes("/faqs")) return "faqs";
  if (path.includes("/offers")) return "offers";
  if (path.includes("/notification")) return "notifications";
  if (path.includes("/reports")) return "reports";
  if (path.includes("/settings")) return "settings";
  if (path.includes("/tax")) return "tax";

  // MASTER GROUP
  if (path.includes("/expenses")) return "master";
  if (path.includes("/expensevouchers")) return "master";
  if (path.includes("/Supplier")) return "Supplier";
  if (path.includes("/payment-voucher")) return "PaymentVouchers";
  if (path.includes("/create-payment")) return "CreatePaymentVoucher";
  if (path.includes("/states")) return "StatesPage";
  if (path.includes("/districts")) return "Districts";
  if (path.includes("/mandals")) return "Mandals";
  if (path.includes("/cities")) return "Cities";
  if (path.includes("/purchase-vouchers")) return "PurchaseVouchers";
  if (path.includes("/create-purchase")) return "CreatePurchaseVoucher";
  if (path.includes("/items")) return "Items";

  
  
  return "dashboard";
};

/* -------------------- Protected App Layout -------------------- */
const ProtectedAppLayout = ({ children }) => {
  const { token, isReady } = useAuth();
  const location = useLocation();

  if (!isReady) return null;

  const publicRoutes = [
    "/dashboard/login",
    "/dashboard/forgot-password",
    "/dashboard/validate-otp",
    "/dashboard/reset-password",
  ];

  // Allow public pages
  if (!token && !publicRoutes.includes(location.pathname)) {
    return <Navigate to="/dashboard/login" replace />;
  }

  // No dashboard layout for public pages
  if (publicRoutes.includes(location.pathname)) {
    return children;
  }

  return (
    <DashboardLayout activeSection={getActiveSection(location.pathname)}>
      {children}
    </DashboardLayout>
  );
};

/* -------------------- Pages -------------------- */
const DashboardHome = () => (
  <div className="p-6 max-w-7xl mx-auto">
    <Dashboard />
  </div>
);

// Add these wrapper components near DashboardHome:
const MaterialsHome = () => (
  <div className="p-6 max-w-7xl mx-auto">
    <MaterialsPage />
  </div>
);

const ColoursHome = () => (
  <div className="p-6 max-w-7xl mx-auto">
    <ColoursPage />
  </div>
);

const StylesHome = () => (
  <div className="p-6 max-w-7xl mx-auto">
    <StylesPage />
  </div>
);
/* -------------------- App -------------------- */
function App() {
  return (
    <BrowserRouter basename="/">
      <ScrollToTop/>
      <ProtectedAppLayout>
        <Routes>
          {/* Public */}
          <Route path="/dashboard/login" element={<DashboardLogin />} />
  <Route path="/dashboard/forgot-password" element={<ForgotPassword />} />
<Route path="/dashboard/validate-otp" element={<OtpVerification />} />
<Route path="/dashboard/reset-password" element={<ResetPassword />} />
          {/* Protected */}
          <Route path="/dashboard" element={<DashboardHome />} />
          <Route path="/categories" element={<CategoryPage />} />
          <Route path="/subcategories" element={<SubCategoryPage />} />
          <Route path="/modals" element={<ModalPage />} />
          <Route path="/products" element={<ProductPage />} />
          <Route path="/products/add" element={<AddProductPage />} />
          <Route path="/products/edit/:id" element={<AddProductPage />} />
          <Route path="/producttype" element={<ProductTypesPage />} />

          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/transactions" element={<TransactionsPage />} />
          <Route path="/customers" element={<CustomersPage />} />
          {/* <Route path="/canvas" element={<CanvaTable />} /> */}
          <Route path="/discount" element={<DiscountType />} />
          {/* <Route path="/shippingmethods" element={<ShipmentMethodsTable />} /> */}
          <Route path="/reporttypes" element={<ReportTypesTable />} />
          <Route path="/reports" element={<ReportsTable />} />
          {/* <Route path="/bulksms" element={<BulkSMSPage />} /> */}
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/faqs" element={<FAQPage />} />
          <Route path="/coupons" element={<OffersPage />} />
          <Route path="/notification" element={<NotificationsTable />} />
          {/* <Route path="/tax" element={<TaxPage />} /> */}
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/contact" element={<ContactAdminPage />} />
          <Route path="/kan" element={<Kanbanboard />} />
          <Route path="/blog" element={<BlogAdmin />} />
<Route path="/orders-coming-soon" element={<OrdersComingSoon />} />
<Route path="/customers-coming-soon" element={<CustomersComingSoon />} />
<Route path="/tax" element={<TaxSettingsPage />} />
<Route path="/productdetails" element={<ProductDetails />} />
<Route path="/productattribute" element={<ProductAttributes />} />

<Route path="/product-attributes/materials" element={<MaterialsHome />} />
<Route path="/product-attributes/colours"   element={<ColoursHome />} />
<Route path="/product-attributes/styles"    element={<StylesHome />} />
<Route path="/order-types"    element={<OrderTypesPage />} />

          {/* Redirects */}
          <Route path="/" element={<Navigate to="/dashboard/login" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />


          <Route path="/expenses" element={<ExpensesPage />} />
          <Route path="/expensevouchers" element={<ExpenseVoucherPage />} />
          <Route path="/suppliers" element={<Supplier />} />
          <Route path="/payment-voucher" element={<PaymentVouchers />} />
          <Route path="/create-payment" element={<CreatePaymentVoucher />} />
          <Route path="/states" element={<StatesPage />} />
          <Route path="/districts" element={<Districts />} />
          <Route path="/mandals" element={<Mandals />} />
          <Route path="/cities" element={<Cities />} />
          <Route path="/purchase-voucher" element={<PurchaseVouchers />} />
          <Route path="/create-purchase" element={<CreatePurchaseVoucher />} />
          <Route path="/items" element={<Items />} />

          
          

          
       
        </Routes>
      </ProtectedAppLayout>
    </BrowserRouter>
  );
}

export default App;
